/**
 * For authors of custom elements, this module lets you define an element slot
 * within your custom element class to be correctly consumed within and outside
 * the element definition. The method must be called within the
 * `connectedCallback()` of your custom element class. It takes care of defining
 * a generic `slots()` to get/set element slots, which you can then access
 * within your class definition.
 *
 * Slots can be named or unnamed. Named slots can be accessed by calling
 * `this.slots('my-slot-name')`. Unnamed slots can be accessed by
 * `this.slots()`. Both calls returns an array list of child nodes that are
 * either top-level child nodes for unnamed slots or child nodes that match the
 * named slot. Unnamed slots always excludes child nodes that are assigned to a
 * named slot, i.e. those that have a slot attribute. Given these accessors to
 * slots defined at the consumer level, you are responsible for inserting them
 * into your master template.
 *
 * Outside consumers may access these defined slots and potentially replace them
 * with different elements if needed by passing in your own list of slot nodes
 * as the second argument to the slots method, e.g. `this.slots('title',
 * element)`. This is not a common pattern however. Slots should ideally be
 * defined only via markup instead.
 *
 * For example:
 * class MyElement extends LitElement {
 *   connectedCallback () {
 *     super.connectedCallback()
 *     defineElementSlot(this)            // Unnamed slot
 *     defineElementSlot(this, 'title')   // Named slot
 *   }
 *
 *   render () {
 *     // Access and insert my defined slots using the `slots()` method.
 *     return html`<div>
 *       <header>${this.slots('title'}</header>
 *       <div>${this.slots()}</div>
 *     </div>`
 *   }
 * }
 *
 * Consumers can then define slots like so. For named slots, consumers may
 * assign more than one element, which simply gets appended in that order.
 *
 * <my-element>
 *   This is the normal content body that will get pushed into the unnamed slot.
 *   <h2 slot='title'>Hello there</h2>
 *   <p slot='title'>I will be appended to the content slot</p>
 *   <p slot='title'>Another appended node to the content slot</p>
 * </my-element>
 *
 * @param {Object} instance Custom element instance to define element slot in.
 * @param {string} slotName Optional name of the slot to define. If provided, a
 *  named slot is defined and will capture the named slot from consumers. If
 *  omitted, an unnamed slot is defined with all child nodes (exept elements
 *  belonging to a slot) at the root level declared by consumers.
 */
export default function defineElementSlot (instance, slotName) {
  // Normalize unnamed slots to always be stored with an "undefined" key.
  slotName = slotName || undefined

  // Initialize the slots map that holds reference to all defined slots.
  instance._slotMap = instance._slotMap || {}

  // If the given slot name was already initialized, do nothing more.
  if (instance._slotMap[slotName]) return

  // Initialize the slot elements and store them in our map.
  instance._slotMap[slotName] = initSlotElement.call(instance, slotName)

  // Define a method that consumers can call to access or update their slots. We
  // should only ever do this once.
  if (!instance.slots) {
    Object.defineProperty(instance, 'slots', {
      configurable: false,
      enumerable: false,
      writable: false,
      value (name, element) {
        if (element) {
          this._slotMap[name] = makeElementSlot.call(this, name, element)
        }
        return this._slotMap[name]
      }
    })
  }
}

/**
 * Returns a list of slot elements for the specified slot name. If slot name is
 * not specified, then all top-level child nodes that do not belong to a named
 * slot are returned.
 * @param {string} name Optional, depending if slot is named or unnamed.
 * @return {Array.<HTMLElement>} All top-level child nodes that aren't slotted.
 */
function initSlotElement (name) {
  let slot

  if (name) {
    slot = makeElementSlot.call(this, name)
  } else {
    // For unnamed slots, the slot is an array of all top-level child elements
    // that do not have slot attributes. We must not filter out #text or
    // #comment nodes b/c that is what lit-element uses to track changes.
    slot = Array.from(this.childNodes).filter(node => !node.slot)
  }

  return slot
}

/**
 * Normalizes the given elements and prepares them to look and behave like a
 * slot elements, ensuring they contain all necessary attributes that one would
 * expect slot elements to have.
 * @param {string} name Slot name
 * @param {HTMLElement} element Optional element to use instead of querying it.
 * @return {Array.<HTMLElement} All slot elements that match the slot name.
 */
function makeElementSlot (name, elements) {
  elements = elements || Array.from(this.querySelectorAll(`[slot='${name}']`))

  elements.forEach(element => {
    // If element is not an HTML element, don't do anything further.
    if (!element.setAttribute) return element

    // Ensure slot element always has its slot property return the slot name.
    element.slot = name
  })

  return elements
}
